import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { skillApi } from '@/services/skillKnowledgeApi';
import { useEntity, useMutation } from '@/hooks';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { ArrowLeft, Edit, Trash2, Power, PowerOff } from 'lucide-react';
import { LoadingState, ErrorState, CopyableId } from '@/components/common';
import type { Skill } from '@/types/skillKnowledge';

export const SkillDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useAdminAuth();

  const canEdit = hasPermission('edit', 'skill');
  const canDelete = hasPermission('delete', 'skill');
  const canActivate = hasPermission('activate', 'skill');
  const canDeactivate = hasPermission('deactivate', 'skill');

  // Use entity hook for fetching skill
  const {
    data: skill,
    isLoading,
    error,
    refetch,
  } = useEntity<Skill>({
    fetchFn: (skillId: string) => skillApi.getById(skillId),
    entityId: id,
    entityName: 'skill',
    enabled: !!id,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('Skill ID is required');
      await skillApi.delete(id);
    },
    entityName: 'skill',
    operation: 'delete',
    onSuccess: () => {
      navigate('/admin/skills');
    },
  });

  // Activate mutation
  const activateMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('Skill ID is required');
      await skillApi.activate(id);
    },
    entityName: 'skill',
    operation: 'update',
    successMessage: 'Skill activated successfully',
    onSuccess: () => {
      refetch();
    },
  });

  // Deactivate mutation
  const deactivateMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('Skill ID is required');
      await skillApi.deactivate(id);
    },
    entityName: 'skill',
    operation: 'update',
    successMessage: 'Skill deactivated successfully',
    onSuccess: () => {
      refetch();
    },
  });

  const handleDelete = async () => {
    if (!id || !window.confirm('Are you sure you want to delete this skill?')) return;
    await deleteMutation.mutate();
  };

  const handleActivate = async () => {
    if (!id) return;
    await activateMutation.mutate();
  };

  const handleDeactivate = async () => {
    if (!id) return;
    await deactivateMutation.mutate();
  };

  if (isLoading) {
    return <LoadingState message="Loading skill..." />;
  }

  if (error || !skill) {
    return <ErrorState error={error || new Error('Skill not found')} onRetry={refetch} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/skills">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{skill.name}</h1>
            <p className="text-muted-foreground mt-2">Skill Details</p>
          </div>
        </div>
        <div className="flex gap-2">
          {canActivate && !skill.isActive && (
            <Button variant="outline" onClick={handleActivate} disabled={activateMutation.isLoading}>
              <Power className="mr-2 h-4 w-4" />
              Activate
            </Button>
          )}
          {canDeactivate && skill.isActive && (
            <Button variant="outline" onClick={handleDeactivate} disabled={deactivateMutation.isLoading}>
              <PowerOff className="mr-2 h-4 w-4" />
              Deactivate
            </Button>
          )}
          {canEdit && (
            <Button asChild>
              <Link to={`/admin/skills/${id}/edit`}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Link>
            </Button>
          )}
          {canDelete && (
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isLoading}>
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <CopyableId id={skill.id || id || ''} label="Skill ID" />
            <div>
              <label className="text-sm font-medium text-muted-foreground">Name</label>
              <p className="mt-1">{skill.name}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Description</label>
              <p className="mt-1">{skill.description}</p>
            </div>
            {skill.summary && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Summary</label>
                <p className="mt-1">{skill.summary}</p>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-muted-foreground">Category</label>
              <p className="mt-1">{skill.category}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Domain</label>
              <p className="mt-1">{skill.domain}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Status</label>
              <p className="mt-1">
                <span
                  className={`px-2 py-1 rounded-full text-xs ${
                    skill.status === 'ACTIVE'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : skill.status === 'DRAFT'
                      ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                      : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                  }`}
                >
                  {skill.status}
                </span>
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Active</label>
              <p className="mt-1">{skill.isActive ? 'Yes' : 'No'}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Skill Levels & Metrics</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Available Skill Levels</label>
              <p className="mt-1">{skill.availableSkillLevels?.join(', ') || 'N/A'}</p>
            </div>
            {skill.expectedLearningHours && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Expected Learning Hours</label>
                <p className="mt-1">{skill.expectedLearningHours} hours</p>
              </div>
            )}
            {skill.difficultyRating && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Difficulty Rating</label>
                <p className="mt-1">{skill.difficultyRating}/10</p>
              </div>
            )}
            {skill.popularityScore !== undefined && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Popularity Score</label>
                <p className="mt-1">{skill.popularityScore.toFixed(2)}</p>
              </div>
            )}
            {skill.parentSkillId && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Parent Skill ID</label>
                <p className="mt-1 font-mono text-sm">{skill.parentSkillId}</p>
              </div>
            )}
            {skill.childSkillIds && skill.childSkillIds.length > 0 && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Child Skill IDs</label>
                <div className="mt-1 space-y-1">
                  {skill.childSkillIds.map((childId) => (
                    <p key={childId} className="font-mono text-sm">
                      {childId}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {skill.tags && skill.tags.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Tags</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {skill.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-secondary text-secondary-foreground rounded-md text-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {(skill.prerequisiteSkills && skill.prerequisiteSkills.length > 0) ||
        (skill.enablesSkills && skill.enablesSkills.length > 0) ? (
          <Card>
            <CardHeader>
              <CardTitle>Skill Relationships</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {skill.prerequisiteSkills && skill.prerequisiteSkills.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Prerequisite Skills</label>
                  <div className="mt-2 space-y-2">
                    {skill.prerequisiteSkills.map((prereqSkillId) => (
                      <div key={prereqSkillId} className="border rounded-lg p-3 space-y-2">
                        <Link
                          to={`/admin/skills/${prereqSkillId}`}
                          className="font-medium text-blue-600 hover:underline"
                        >
                          View Skill
                        </Link>
                        <CopyableId id={prereqSkillId} label="Skill ID" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {skill.enablesSkills && skill.enablesSkills.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Enables Skills</label>
                  <div className="mt-2 space-y-2">
                    {skill.enablesSkills.map((enablesSkillId) => (
                      <div key={enablesSkillId} className="border rounded-lg p-3 space-y-2">
                        <Link
                          to={`/admin/skills/${enablesSkillId}`}
                          className="font-medium text-blue-600 hover:underline"
                        >
                          View Skill
                        </Link>
                        <CopyableId id={enablesSkillId} label="Skill ID" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ) : null}

        <Card>
          <CardHeader>
            <CardTitle>Audit Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Created At</label>
              <p className="mt-1">
                {skill.createdAt
                  ? (Array.isArray(skill.createdAt)
                      ? new Date(skill.createdAt[0], (skill.createdAt[1] || 1) - 1, skill.createdAt[2] || 1, skill.createdAt[3] || 0, skill.createdAt[4] || 0, skill.createdAt[5] || 0).toLocaleString()
                      : new Date(skill.createdAt).toLocaleString())
                  : 'N/A'}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Updated At</label>
              <p className="mt-1">
                {skill.updatedAt
                  ? (Array.isArray(skill.updatedAt)
                      ? new Date(skill.updatedAt[0], (skill.updatedAt[1] || 1) - 1, skill.updatedAt[2] || 1, skill.updatedAt[3] || 0, skill.updatedAt[4] || 0, skill.updatedAt[5] || 0).toLocaleString()
                      : new Date(skill.updatedAt).toLocaleString())
                  : 'N/A'}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Created By</label>
              <p className="mt-1">{skill.createdBy || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Last Modified By</label>
              <p className="mt-1">{skill.updatedBy || 'N/A'}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SkillDetailPage;
